import React, { useState } from 'react';
import { Card, Button, Flex, Typography, theme, Modal, Dropdown, notification, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, CopyOutlined, MoreOutlined, TeamOutlined } from '@ant-design/icons';
import { Queue } from '../../../../../../types/queue';

import { createLogger } from '../../../../../../utils/logger';
const logger = createLogger('features-assistants');

const { useToken } = theme;
const { Text } = Typography;

interface CardQueueActionProps {
  message: string;
  onDelete: () => void;
  onDuplicate: () => void;
  onEdit: (newMessage: string) => void;
  chatbotId?: string;
  flowId?: string;
}

export const CardQueueAction: React.FC<CardQueueActionProps> = ({
  message,
  onDelete,
  onDuplicate,
  onEdit,
  chatbotId,
  flowId
}) => {
  const { token } = useToken();
  const [showEditModal, setShowEditModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Extraer los datos de la cola del mensaje
  const extractQueueData = (msg: string) => {
    try {
      const match = msg.match(/\{.*\}/);
      if (match) {
        const queueData = JSON.parse(match[0]);
        return {
          type: queueData.type || 'queue',
          queueId: queueData.queueId || '',
          queueName: queueData.queueName || '',
          chatbotId: queueData.chatbotId || '',
          action: queueData.action || 'assign',
          description: queueData.description || '',
          timestamp: queueData.timestamp || new Date().toISOString()
        };
      }
      return {
        type: 'queue',
        queueId: '',
        queueName: '',
        chatbotId: '',
        action: 'assign',
        description: '',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error('Error parsing queue message:', error);
      return {
        type: 'queue',
        queueId: '',
        queueName: '',
        chatbotId: '',
        action: 'assign',
        description: '',
        timestamp: new Date().toISOString()
      };
    }
  };
  
  const queueData = extractQueueData(message);
  
  const handleEditClick = () => {
    setShowEditModal(true);
  };
  
  const handleFormSave = (newMessage: string) => {
    onEdit(newMessage);
    setShowEditModal(false);
  };
  
  const handleFormCancel = () => {
    setShowEditModal(false);
  };

  const handleDeleteClick = async () => {
    setIsDeleting(true);
    try {
      // Para colas, solo eliminar el mensaje del flujo
      onDelete();
      notification.success({
        message: 'Success',
        description: 'Queue assignment removed from flow'
      });
    } catch (error: any) {
      logger.error('Error in delete process:', error);
      notification.error({
        message: 'Error',
        description: error?.message || 'Failed to remove queue assignment'
      });
    } finally {
      setIsDeleting(false);
    }
  };
  
  const menuItems = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: handleEditClick
    },
    {
      key: 'duplicate',
      label: 'Duplicate',
      icon: <CopyOutlined />,
      onClick: onDuplicate
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      onClick: handleDeleteClick,
      disabled: isDeleting
    }
  ];

  const getActionColor = (action: string) => {
    switch (action) {
      case 'assign':
        return 'blue';
      case 'join':
        return 'green';
      case 'leave':
        return 'red';
      default:
        return 'default';
    }
  };

  const getActionText = (action: string) => {
    switch (action) {
      case 'assign':
        return 'Assign to Queue';
      case 'join':
        return 'Join Queue';
      case 'leave':
        return 'Leave Queue';
      default:
        return action;
    }
  };
  
  return (
    <>
      <Card 
        style={{ 
          maxWidth: '400px',
          width: '100%',
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowTertiary,
          cursor: 'pointer'
        }}
        bodyStyle={{ padding: token.paddingSM }}
        onClick={handleEditClick}
      >
        <Flex align="center" justify="space-between" style={{ width: '100%' }}>
          <Flex gap={token.marginMD} align="center">
            <div 
              style={{ 
                width: 120,
                height: 70,
                backgroundColor: '#1890ff',
                borderRadius: token.borderRadiusLG,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
                textAlign: 'center',
                padding: '8px'
              }}
            >
              <TeamOutlined style={{ fontSize: '24px', marginRight: '8px' }} />
              Queue
            </div>
            <Flex vertical gap={token.marginXS}>
              <Flex align="center" gap={token.marginXS}>
                <Text strong style={{ fontSize: '16px', lineHeight: '24px', margin: 0 }}>
                  {queueData.queueName || 'Queue Assignment'}
                </Text>
                <Tag color={getActionColor(queueData.action)}>
                  {getActionText(queueData.action)}
                </Tag>
              </Flex>
              <Text type="secondary" style={{ fontSize: '14px', lineHeight: '20px', margin: 0 }}>
                {queueData.description || 'Assign conversation to queue'}
              </Text>
            </Flex>
          </Flex>
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button 
                icon={<MoreOutlined />} 
                size="small"
                type="text"
                loading={isDeleting}
              />
            </Dropdown>
          </div>
        </Flex>
      </Card>
      
      <Modal
        title={`Edit Queue Assignment: ${queueData.queueName}`}
        open={showEditModal}
        onCancel={handleFormCancel}
        footer={null}
        width={600}
        destroyOnClose={true}
      >
        <div style={{ padding: token.paddingMD }}>
          <Text>Queue assignments are managed through the Queue Selector. Please use the main flow to modify this assignment.</Text>
          <Flex gap={token.marginMD} style={{ marginTop: token.marginLG }}>
            <Button onClick={handleFormCancel}>
              Close
            </Button>
          </Flex>
        </div>
      </Modal>
    </>
  );
}; 